import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import StyledSelect from '../../components/forms/styledSelect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import FormInput from '../../components/forms/formInput';
import OverlayLoader from '../../components/overlayLoader';
import MaterialCategorySelect from '../../components/selects/materialCategorySelect';
import { createMaterial, getMaterialById, updateMaterial } from '../../services/materials';
import { trimProps } from '../../utils/helper';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const BoxIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

const initMaterial = {
    name: '',
    sku: '',
    categoryId: '',
    unit: 'piece',
    purchasePrice: 0,
    sellingPrice: 0,
    taxRate: 0,
    currentStock: 0,
    minimumStock: 0,
    trackStock: true,
    disabled: false,
}

const SetMaterialModal = ({
    display,
    toggle,
    materialId,
}) => {
    const { t } = useTranslation();

    const [material, setMaterial] = useState({})
    const [autoSku, setAutoSku] = useState(true)

    const queryClient = useQueryClient()
    const { mutate, isLoading } = useMutation(materialId ? updateMaterial : createMaterial, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['materials'])
            onHide()
        })
    })

    const { data, isFetching } = useQuery(['materials', 'details', materialId], () => materialId && getMaterialById(materialId), {
        staleTime: 1000 * 60 * 2,
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            const _material = trimProps(result, initMaterial, ['id'])
            setMaterial(_material)
        })
    }, [data])

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        const payload = { ...material }
        payload.purchasePrice = parseFloat(payload.purchasePrice) || 0
        payload.sellingPrice = parseFloat(payload.sellingPrice) || 0
        payload.taxRate = parseFloat(payload.taxRate) || 0
        payload.currentStock = parseFloat(payload.currentStock) || 0
        payload.minimumStock = parseFloat(payload.minimumStock) || 0
        if (autoSku) payload.sku = null

        mutate(payload)
    }

    const onShow = () => {
        if (!materialId) {
            setMaterial(initMaterial)
            setAutoSku(true)
        } else {
            setAutoSku(false)
        }
    }

    const onHide = () => {
        toggle(false)
    }

    const handleChange = (objValue) => {
        setMaterial({ ...material, ...objValue })
    }

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            title={materialId ? t('materialsPage.editMaterialTitle') : t('materialsPage.addMaterialTitle')}
            subtitle={materialId ? t('materialsPage.editSubtitle') : t('materialsPage.addSubtitle')}
            icon={<BoxIcon />}
            backdrop={isLoading ? 'static' : true}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
                    <button type='submit' form="set-material" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                        {isLoading ? t('materialsPage.submitting') : (materialId ? t('materialsPage.saveMaterial') : t('materialsPage.addMaterialTitle'))}
                    </button>
                </>
            }
        >
            <Form id='set-material' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <Row className="mb-3">
                    <Col xs="12">
                        <FormInput className="form-control mb-2" label={t('materialsPage.materialNameLabel')} value={material.name ?? ''}
                            onChange={(e) => handleChange({ name: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('materialsPage.sku')}</Form.Label>
                            <div className="inp" style={{ opacity: autoSku ? 0.6 : 1, transition: 'opacity .14s', display: 'flex', alignItems: 'center', flexWrap: 'nowrap', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '11px 13px', boxShadow: 'var(--sh-1)' }}>
                                <input
                                    placeholder={autoSku ? t('materialsPage.autoGenerated') : t('materialsPage.skuExample')}
                                    value={autoSku ? '' : (material.sku ?? '')}
                                    onChange={(e) => handleChange({ sku: e.target.value })}
                                    disabled={autoSku}
                                    style={{ background: 'transparent', flex: 1, minWidth: 0, border: 0, outline: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}
                                />
                                <label htmlFor="auto-sku-switch" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, cursor: 'pointer', margin: 0, fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>
                                    <span>{t('materialsPage.auto')}</span>
                                    <div
                                        onClick={() => { setAutoSku(!autoSku); if (autoSku) handleChange({ sku: '' }) }}
                                        style={{
                                            width: 36, height: 20, borderRadius: 999, cursor: 'pointer',
                                            background: autoSku ? 'var(--brand)' : 'var(--line)',
                                            position: 'relative', transition: 'background .18s', flexShrink: 0,
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute', top: 2, left: autoSku ? 18 : 2,
                                            width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .18s',
                                        }} />
                                    </div>
                                </label>
                            </div>
                        </Form.Group>
                    </Col>
                    <Col lg="6">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('materialsPage.unit')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect required value={material.unit ?? ''} onChange={(e) => handleChange({ unit: e.target.value })}>
                                <option value="" disabled>{t('materialsPage.selectUnit')}</option>
                                <option value="piece">piece</option>
                                <option value="kg">kg</option>
                                <option value="litre">litre</option>
                                <option value="m">m</option>
                                <option value="m2">m2</option>
                                <option value="box">box</option>
                                <option value="pack">pack</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                    <Col lg="12">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('materialsPage.category')} <span className="text-danger">*</span></Form.Label>
                            <MaterialCategorySelect required value={material.categoryId ?? ''} onChange={(option) => handleChange({ categoryId: option?.value || '' })} />
                        </Form.Group>
                    </Col>

                    <Col xs="12"><hr className="my-2" /><div className="sub mb-2" style={{ fontWeight: 600, fontSize: '12px' }}>{t('materialsPage.pricing')}</div></Col>
                    <Col lg="4">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('materialsPage.purchasePrice')} value={material.purchasePrice ?? ''}
                            onChange={(e) => handleChange({ purchasePrice: e.target.value })} required
                        />
                    </Col>
                    <Col lg="4">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('materialsPage.sellingPrice')} value={material.sellingPrice ?? ''}
                            onChange={(e) => handleChange({ sellingPrice: e.target.value })} required
                        />
                    </Col>
                    <Col lg="4">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('materialsPage.taxRate')} value={material.taxRate ?? ''}
                            onChange={(e) => handleChange({ taxRate: e.target.value })}
                        />
                    </Col>

                    <Col xs="12"><hr className="my-2" /><div className="sub mb-2" style={{ fontWeight: 600, fontSize: '12px' }}>{t('materialsPage.stockSection')}</div></Col>
                    <Col lg="6">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('materialsPage.currentStock')} value={material.currentStock ?? ''}
                            onChange={(e) => handleChange({ currentStock: e.target.value })}
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('materialsPage.minimumStock')} value={material.minimumStock ?? ''}
                            onChange={(e) => handleChange({ minimumStock: e.target.value })}
                        />
                    </Col>
                    <Col xs="12">
                        <Form.Group className="mt-2">
                            <Form.Check label={t('materialsPage.trackStockLevels')} checked={material.trackStock ?? true}
                                onChange={(e) => handleChange({ trackStock: e.target.checked })} />
                        </Form.Group>
                    </Col>
                    <Col xs="12">
                        <Form.Group className="mt-2">
                            <Form.Check label={t('materialsPage.disableMaterial')} checked={material.disabled ?? false}
                                onChange={(e) => handleChange({ disabled: e.target.checked })} />
                        </Form.Group>
                    </Col>
                </Row>

                {isFetching &&
                    <OverlayLoader />
                }
            </Form>
        </SbrDrawer>
    );
};

export default SetMaterialModal;
